const GSI_SCRIPT = "https://accounts.google.com/gsi/client";

interface GoogleId {
  initialize(config: { client_id: string; callback: (r: { credential: string }) => void; auto_select?: boolean; ux_mode?: "popup" }): void;
  renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
  disableAutoSelect(): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
  }
}

function loadScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google non raggiungibile"));
    document.head.append(script);
  });
}

export class GoogleAuth {
  /** Called when someone signs in or out. */
  onChange: () => void = () => {};
  onError: (message: string) => void = () => {};
  profile: { nickname: string; avatar: string; level: number; xp: number } | null = null;
  private busy = false;

  private readonly clientId: string;
  private readonly accountUrl: string;

  private constructor(clientId: string, accountUrl: string) {
    this.clientId = clientId;
    this.accountUrl = accountUrl;
  }
  /** Asks the server whether sign-in is required; null when it is off (local play, tests). */
  static async fromServer(serverBase: string): Promise<GoogleAuth | null> {
    try {
      const response = await fetch(`${serverBase}/config`, { cache: "no-store" });
      if (!response.ok) return null;
      const config = (await response.json()) as { googleClientId?: unknown };
      if (typeof config.googleClientId !== "string" || !config.googleClientId) return null;
      const auth = new GoogleAuth(config.googleClientId, `${serverBase}/account`);
      const account = await fetch(auth.accountUrl, { credentials: "include", cache: "no-store" }).catch(() => null);
      if (account?.ok) auth.profile = await account.json().catch(() => null);
      return auth;
    } catch {
      return null;
    }
  }

  get signedIn(): boolean {
    return this.profile !== null;
  }

  get firstName(): string {
    return this.profile?.nickname ?? "";
  }
  /** Draws Google's own button in `parent`. */
  async renderButton(parent: HTMLElement): Promise<void> {
    await loadScript();
    const id = window.google!.accounts.id;
    id.initialize({ client_id: this.clientId, callback: (r) => void this.updateSession(r.credential), auto_select: true, ux_mode: "popup" });
    id.renderButton(parent, { theme: "filled_black", size: "large", text: "signin_with", locale: "it", width: 260 });
  }

  signOut(): void {
    void this.updateSession();
  }

  private async updateSession(credential?: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const response = await fetch(this.accountUrl, {
        credentials: "include",
        ...(credential
          ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ credential }) }
          : { method: "DELETE" }),
      });
      if (!response.ok) throw new Error("Account request failed");
      this.profile = credential ? await response.json() : null;
      if (!credential) window.google?.accounts.id.disableAutoSelect();
      this.onError("");
      this.onChange();
    } catch {
      this.onError(credential ? "Accesso non riuscito. Riprova." : "Disconnessione non riuscita. Riprova.");
    } finally {
      this.busy = false;
    }
  }
}
